import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {IconButton, Input, Typography, useTheme, Button} from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {alpha, styled} from '@mui/material/styles';
import {useEffect, useState} from 'react';
import Collapse from '@mui/material/Collapse';

import {StoredFilter} from 'filters/features/types';
import {RequireFirstOrSecond} from 'types';

import {shouldNotForwardProp} from 'utils/styled';
import {confirmation, ConfirmationRejectReason} from 'utils/confirmation';

import CheckIcon from 'components/icons/check.svg';
import EditIcon from 'components/icons/edit/edit.svg';
import RearrangeIcon from 'components/icons/rearrange.svg';
import TrashIcon from 'components/icons/trash.svg';
import {OverflownTooltip} from 'components/OverflownTooltip';
const SRoot = styled(
  'div',
  shouldNotForwardProp('focusBorder', 'selected', 'isDragging', 'dragOverlay', 'isPending')
)<{focusBorder?: boolean; selected?: boolean; isDragging?: boolean; dragOverlay?: boolean; isPending?: boolean}>(
  ({focusBorder, theme, selected, isDragging, dragOverlay, isPending}) => ({
    ...theme.typography.subtitle1,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(0, 3),
    opacity: isDragging ? 0.7 : 1,

    borderRadius: theme.shape.borderRadiusRegular,
    justifyContent: 'space-between',

    [`&:not(:hover) ${SActions}`]: {
      opacity: 0,
    },

    ...(isPending && {
      [`&:not(:hover) ${SActions}`]: {
        opacity: 1,
      },
    }),

    ':focus-within': {
      outline: focusBorder ? `1px solid ${theme.palette.primary.main}` : 'none',
      outlineOffset: -1,
    },

    ':hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),

      [`& ${SActiveIcon}`]: {
        opacity: 0,
      },
    },

    ...(dragOverlay && {
      [`${SActions}`]: {
        opacity: 0,
      },
      [`:hover ${SActiveIcon}`]: {
        opacity: 1,
      },
    }),

    ...(selected && {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    }),
  })
);

const SRearrangeButton = styled(IconButton)(({theme}) => ({
  padding: theme.spacing(1, 1.5),
  cursor: 'grab',
  color: theme.palette.text.secondary,
}));

const STitleButton = styled('button')(({disabled}) => ({
  all: 'unset',
  overflow: 'hidden',
  height: '100%',
  flexGrow: 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
}));

const STitleInput = styled(Input)(({theme}) => ({
  ...theme.typography.subtitle1,
  padding: theme.spacing(2.5, 3),
  paddingRight: 0,
  flexGrow: 1,

  '& > input': {
    padding: 0,
  },
}));

const SActionsWrapper = styled('div')(() => ({
  position: 'relative',
  flexShrink: 0,
}));

const SActions = styled('div')(() => ({}));

const SActiveIcon = styled('div')(({theme}) => ({
  position: 'absolute',
  top: '50%',
  right: theme.spacing(2),
  transform: 'translateY(-50%)',
  height: 16,
  display: 'flex',
}));

type SavedFiltersItemProps = StoredFilter &
  RequireFirstOrSecond<
    {
      onDelete: () => void;
    },
    {
      dragOverlay: boolean;
    }
  > & {
    onSelect?: () => void;
    onRename?: (newName: string) => Promise<void>;
    disabled?: boolean;
    selected?: boolean;
    active?: boolean;
    isDndActive?: boolean;
    names?: string[];
  };

export const SavedFiltersItem = ({
  id,
  name,
  selected,
  onSelect,
  onDelete,
  onRename,
  active,
  isDndActive,
  dragOverlay,
  disabled,
  names,
}: SavedFiltersItemProps) => {
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameMode, setRenameMode] = useState(false);
  const [newName, setNewName] = useState(name);
  const theme = useTheme();

  const {isDragging, attributes, listeners, transform, transition, setNodeRef} = useSortable({id});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    renameMode && setNewName(name);
  }, [name, renameMode]);

  useEffect(() => {
    setRenameMode(false);
  }, [isDndActive]);

  const handleDelete = async () => {
    if (!onDelete) {
      return;
    }

    try {
      await confirmation({
        content: 'Are you sure you want to delete this filter?',
      });

      setDeleteLoading(true);
      onDelete();
      setDeleteLoading(false);
    } catch (e) {
      if ((e as ConfirmationRejectReason) === 'cancel') {
        return;
      }
    }
  };

  const handleRenameSave = async () => {
    setRenameLoading(true);
    await onRename?.(newName);
    setRenameLoading(false);
    setRenameMode(false);
  };

  if (renameMode) {
    return (
      <ClickAwayListener onClickAway={renameLoading ? () => {} : () => setRenameMode(false)}>
        <div>
          <SRoot selected={selected} focusBorder style={style} ref={setNodeRef}>
            <STitleInput
              autoFocus
              disableUnderline
              value={newName}
              onChange={e => setNewName(e.target.value)}
              readOnly={renameLoading}
              inputProps={{maxLength: 100}}
            />
            <SActionsWrapper>
              <Button
                disabled={newName === name || names?.includes(newName) || !newName.trim()}
                size='small'
                loading={renameLoading}
                onClick={handleRenameSave}
              >
                Save
              </Button>
            </SActionsWrapper>
          </SRoot>
          <Collapse in={names?.includes(newName) && newName !== name}>
            <Typography variant='body3' color='error.main' paddingLeft={6}>
              Name should be unique
            </Typography>
          </Collapse>
          <Collapse in={!newName.trim()}>
            <Typography variant='body3' color='error.main' paddingLeft={6}>
              Please name your filter before saving
            </Typography>
          </Collapse>
        </div>
      </ClickAwayListener>
    );
  }

  return (
    <SRoot
      selected={selected}
      isPending={deleteLoading}
      style={style}
      ref={setNodeRef}
      isDragging={isDragging}
      dragOverlay={dragOverlay}
    >
      {!disabled && (
        <SRearrangeButton {...attributes} {...listeners} size='small' disabled={renameLoading || deleteLoading}>
          <RearrangeIcon />
        </SRearrangeButton>
      )}

      <OverflownTooltip<HTMLButtonElement> title={name}>
        {ref => (
          <STitleButton ref={ref} type='button' onClick={onSelect} disabled={disabled}>
            <Typography variant='subtitle1' noWrap>
              {name}
            </Typography>
          </STitleButton>
        )}
      </OverflownTooltip>

      <SActionsWrapper>
        {active && (
          <SActiveIcon>
            <CheckIcon color={theme.palette.primary.main} width={16} height={16} />
          </SActiveIcon>
        )}

        <SActions>
          {!disabled && (
            <Button size='small' color='secondary' onClick={() => setRenameMode(true)} disabled={deleteLoading}>
              <EditIcon width={16} height={16} />
            </Button>
          )}

          {onDelete && (
            <Button size='small' color='secondary' loading={deleteLoading} onClick={handleDelete}>
              <TrashIcon width={16} height={16} />
            </Button>
          )}
        </SActions>
      </SActionsWrapper>
    </SRoot>
  );
};
